import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LineSketch } from "@/components/quote/LineSketch";

// Every valid fold for n panels: all left, all right, then each left/right split.
export const foldsFor = (n) => [`${n}L`, `${n}R`, ...Array.from({ length: n - 1 }, (_, i) => `${n - i - 1}L${i + 1}R`)];

// Tiles use a fixed per-panel shape (not the line's real size) so every fold reads clearly, even on a narrow unit.
const tileSize = (n) => ({ w: n * 24, h: 80 });

// Outside-view drawing of one fold, by the same backend sketch code as the PDF (no dimensions, bold strokes).
function Tile({ f, on, n, disabled, onPick, testId }) {
  return (
    <button type="button" role="radio" aria-checked={on} disabled={disabled} data-testid={testId} onClick={onPick}
      className={`flex min-h-[44px] flex-col items-center gap-1 rounded-xl border-2 px-1.5 pb-1.5 pt-2 transition-colors ${on ? "border-[#0071E3] bg-[#E8F1FD]" : "border-black/10 bg-white hover:border-[#0071E3]/50"} ${disabled ? "cursor-default opacity-40" : ""}`}>
      <LineSketch params={{ sketch: "bifold", mapped: true, ...tileSize(n), config: f, dims: false, bold: true }} boxW={96} boxH={72} className="h-[60px] w-[80px] bg-transparent" />
      <span className={`font-mono text-[13px] font-bold ${on ? "text-[#0071E3]" : "text-black"}`}>{f}</span>
    </button>
  );
}

function Grid({ n, value, onChange, readOnly, testId, cols }) {
  return (
    <div className={`grid gap-2 ${cols}`} role="radiogroup" aria-label="Bi-fold fold">
      {foldsFor(n).map((f) => (
        <Tile key={f} f={f} on={f === value} n={n} disabled={readOnly && f !== value} testId={`${testId}-tile-${f}`} onPick={() => !readOnly && onChange(f)} />
      ))}
    </div>
  );
}

// Bi-fold fold picker. inline: tiles in a wrap grid (Designer). Otherwise (narrow quote column): the chosen tile + a popover grid.
export function FoldPicker({ panels, value, onChange, readOnly, testId = "fold-picker", inline = false }) {
  const n = Math.max(2, Math.min(8, Number(panels) || 3));
  const [open, setOpen] = useState(false);
  const props = { n, value, readOnly, testId };
  if (inline) {
    return (
      <div data-testid={testId} className="col-span-full w-full basis-full">
        <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-[#6D6D72]">Fold{readOnly ? " (locked)" : ""}</p>
        <Grid {...props} onChange={onChange} cols="grid-cols-[repeat(auto-fill,minmax(92px,1fr))]" />
      </div>
    );
  }
  return (
    <div data-testid={testId} className="w-full basis-full">
      <p className="mb-1 text-xs font-semibold text-slate-400">Fold{readOnly ? " (locked)" : ""}</p>
      <Popover open={open && !readOnly} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={readOnly} data-testid={`${testId}-open`}
            className="flex min-h-[44px] items-center gap-2 rounded-xl border-2 border-[#0071E3] bg-[#E8F1FD] py-1 pl-1 pr-3 disabled:cursor-default">
            <LineSketch params={{ sketch: "bifold", mapped: true, ...tileSize(n), config: value, dims: false, bold: true }} boxW={96} boxH={72} className="h-[48px] w-[64px] bg-transparent" />
            <span className="font-mono text-[14px] font-bold text-[#0071E3]" data-testid={`${testId}-value`}>{value}</span>
            {!readOnly && <span className="flex items-center text-xs font-semibold text-[#0071E3]">Change <ChevronDown className="h-3.5 w-3.5" /></span>}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(92vw,460px)] p-3" data-testid={`${testId}-sheet`}>
          <p className="mb-2 text-sm font-semibold text-[#1D1D1F]">Choose fold · {n} panels</p>
          <Grid {...props} onChange={(f) => { onChange(f); setOpen(false); }} cols={n >= 5 ? "grid-cols-4" : "grid-cols-3 sm:grid-cols-5"} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
